"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

const cards = [
    {
        col: 'md:col-span-2',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7" stroke="#3730a3" strokeWidth="1.6"/>
                <path d="M7 10h6M10 7v6" stroke="#3730a3" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        ),
        text: 'Most businesses believe they have a visibility problem. In reality, many have a perception problem.',
        highlight: false,
    },
    {
        col: 'md:col-span-3',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 16C4 16 6 8 10 8C14 8 16 16 16 16" stroke="#3730a3" strokeWidth="1.6" strokeLinecap="round"/>
                <circle cx="10" cy="6" r="2" stroke="#3730a3" strokeWidth="1.6"/>
            </svg>
        ),
        text: "Customers aren't just comparing products, prices, or services. They're comparing trust, familiarity, credibility, and emotional connection. That's where branding becomes a growth lever.",
        highlight: false,
    },
    {
        col: 'md:col-span-5',
        icon: null,
        text: "Your brand shapes that opinion before a customer ever clicks, enquires, or purchases.",
        highlight: true,
    },
    {
        col: 'md:col-span-5',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 15L7 9l4 3 5-7" stroke="#3730a3" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        text: 'We help businesses build brands that people understand, remember, and choose — combining consumer psychology, market research, and creative strategy.',
        highlight: false,
    },
];

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12 } },
};

const item = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } },
};

export default function BrandHero() {
    return (
        <section className="relative overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-40 pb-24"
                style={{
                    background: '#ffffff',
                }}
            >
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundImage: 'radial-gradient(circle, rgba(55,48,163,0.07) 1.5px, transparent 1.5px)',
                        backgroundSize: '32px 32px',
                        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                    }}
                />
                <div className="absolute top-0 right-0 pointer-events-none" style={{ width: 420, height: 420, background: 'radial-gradient(circle, rgba(55,48,163,0.06) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />

                <div className="max-w-[1200px] mx-auto flex flex-col items-center gap-12 relative z-10">

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-sm md:text-base font-semibold tracking-[0.16em] uppercase text-center"
                        style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Branding &amp; Creative Strategy Agency in Bangalore
                    </motion.p>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight text-center"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            color: '#080400',
                        }}
                    >
                        Brands Aren&apos;t Chosen Because<br className="hidden md:block" /> They&apos;re Seen. They&apos;re Chosen Because<br className="hidden md:block" /> They&apos;re Remembered.
                    </motion.h1>

                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="w-full grid grid-cols-1 md:grid-cols-5 gap-3"
                    >
                        {cards.map((card, i) => (
                            <motion.div key={i} variants={item} className={card.col}>
                                {card.highlight ? (
                                    <div
                                        className="w-full rounded-2xl px-7 py-5 flex items-center justify-center"
                                        style={{
                                            background: '#3730a3',
                                            border: '1.5px solid #3730a3',
                                        }}
                                    >
                                        <p
                                            className="text-center font-bold text-base md:text-lg leading-snug"
                                            style={{
                                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                color: '#ffffff',
                                                letterSpacing: '0.02em',
                                            }}
                                        >
                                            {card.text}
                                        </p>
                                    </div>
                                ) : (
                                    <div
                                        className="h-full rounded-2xl px-6 py-5 flex flex-col gap-4"
                                        style={{
                                            background: 'rgba(255,255,255,0.9)',
                                            border: '1.5px solid rgba(55,48,163,0.12)',
                                            boxShadow: '0 4px 24px rgba(55,48,163,0.06)',
                                        }}
                                    >
                                        {card.icon && (
                                            <div
                                                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                                                style={{ background: 'rgba(55,48,163,0.08)', border: '1px solid rgba(55,48,163,0.15)' }}
                                            >
                                                {card.icon}
                                            </div>
                                        )}
                                        <p
                                            className="text-sm md:text-base leading-relaxed"
                                            style={{
                                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                                color: 'rgba(8,4,0,0.65)',
                                                letterSpacing: '0.03em',
                                            }}
                                        >
                                            {card.text}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <Link
                            href="/contact"
                            className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                            style={{
                                background: '#3730a3',
                                color: '#ffffff',
                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                letterSpacing: '0.08em',
                            }}
                        >
                            Book A Brand Strategy Session
                        </Link>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
